import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Award, FileText, Download } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";

const PartnerBeneficiaryDetail: React.FC = () => {
  const { beneficiaryId } = useParams<{ beneficiaryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const beneficiary = location.state?.beneficiary;
  const companyId = location.state?.companyId || localStorage.getItem("partnerCompanyId") || "1";
  const companyType = location.state?.companyType || localStorage.getItem("partnerCompanyType") || "aviation";
  const isAviation = companyType === "aviation";

  // Se não tiver dados no state, usar mock data
  const mockBeneficiary = {
    id: beneficiaryId,
    nome: "João Silva",
    email: "joao@email.com",
    cpf: "123.456.789-00",
    telefone: "(11) 98765-4321",
    cidade: "São Paulo",
    dataCadastro: "15/01/2024",
    status: "Ativo",
    totalRecebido: 1250.50,
    viagens: [
      { id: "1", data: "15/06/2024", rota: "GRU - SDU", distancia: "685 km", classe: "Econômica", emissao: "0,185 kg", msc: 9.25 },
      { id: "2", data: "20/07/2024", rota: "SDU - GRU", distancia: "685 km", classe: "Executiva", emissao: "0,420 kg", msc: 21.00 }
    ],
    checkIns: [
      { id: "1", data: "05/03/2024", origem: "Brasília, DF", transporte: "Aéreo Convencional", distancia: 1060, emissao: 265.0, msc: 13250.0 },
      { id: "2", data: "18/03/2024", origem: "Curitiba, PR", transporte: "Carro", distancia: 410, emissao: 77.9, msc: 3895.0 }
    ]
  };

  const beneficiaryData = beneficiary || mockBeneficiary;

  // Função para calcular CO2 total
  const calcularCO2Total = (): number => {
    let totalCO2Kg = 0;
    
    if (isAviation && beneficiaryData.viagens) {
      beneficiaryData.viagens.forEach((viagem: any) => {
        const emissaoStr = viagem.emissao?.replace(" kg", "").replace(",", ".").trim() || "0";
        totalCO2Kg += parseFloat(emissaoStr) || 0;
      });
    } else if (!isAviation && beneficiaryData.checkIns) {
      beneficiaryData.checkIns.forEach((checkIn: any) => {
        totalCO2Kg += checkIn.emissao || 0;
      });
    }
    
    return totalCO2Kg / 1000; // Converter para toneladas
  };

  const co2Toneladas = calcularCO2Total();
  const mudas = Math.ceil(co2Toneladas / 0.15);
  const isTopTier = beneficiaryData.totalRecebido > 1500;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  const handleGenerateCertificate = () => {
    toast({
      title: "Certificado gerado!",
      description: `Certificado de ${beneficiaryData.nome} está sendo baixado...`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/partner/beneficiaries")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Detalhes do Beneficiário</h1>
          <p className="text-gray-600 mt-1">Informações completas do cliente</p>
        </div>
      </div>

      {/* Header Card com Informações Principais */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar className="h-20 w-20">
                  <AvatarFallback className={isTopTier ? "bg-yellow-100 text-yellow-700 text-2xl" : "bg-[#0e7cb4] text-white text-2xl"}>
                    {getInitials(beneficiaryData.nome)}
                  </AvatarFallback>
                </Avatar>
                {isTopTier && (
                  <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1">
                    <Award className="h-5 w-5 text-yellow-800" />
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">{beneficiaryData.nome}</h2>
                  {isTopTier && (
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-700 border-yellow-300">
                      Top Cliente
                    </Badge>
                  )}
                  <Badge
                    variant="outline"
                    className={
                      beneficiaryData.status === "Ativo"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-600 border-slate-200"
                    }
                  >
                    {beneficiaryData.status}
                  </Badge>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong>Email:</strong> {beneficiaryData.email}</p>
                  <p><strong>CPF:</strong> {beneficiaryData.cpf}</p>
                  {beneficiaryData.telefone && <p><strong>Telefone:</strong> {beneficiaryData.telefone}</p>}
                  {beneficiaryData.cidade && <p><strong>Cidade:</strong> {beneficiaryData.cidade}</p>}
                  {beneficiaryData.dataCadastro && <p><strong>Cadastrado em:</strong> {beneficiaryData.dataCadastro}</p>}
                </div>
              </div>
            </div>
            <Button
              onClick={handleGenerateCertificate}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              <FileText className="mr-2 h-4 w-4" />
              Gerar Certificado
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs de Impacto */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground mb-1">Total Recebido</div>
            <div className="text-2xl font-bold text-[#0e7cb4]">
              {beneficiaryData.totalRecebido.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} MSC
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground mb-1">CO₂ Compensado</div>
            <div className="text-2xl font-bold text-[#0e7cb4]">
              {co2Toneladas > 0 
                ? `${co2Toneladas.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} t`
                : "--"
              }
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground mb-1">Árvores Financiadas</div>
            <div className="text-2xl font-bold text-green-600">
              🌳 {mudas > 0 ? mudas : "--"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground mb-1">{isAviation ? "Viagens" : "Check-ins"}</div>
            <div className="text-2xl font-bold text-[#0e7cb4]">
              {isAviation 
                ? (beneficiaryData.viagens?.length || 0)
                : (beneficiaryData.checkIns?.length || 0)
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs com Histórico */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de {isAviation ? "Viagens" : "Check-ins"}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="historico" className="w-full">
            <TabsList>
              <TabsTrigger value="historico">Histórico Completo</TabsTrigger>
            </TabsList>
            <TabsContent value="historico" className="space-y-4">
              {isAviation && beneficiaryData.viagens && beneficiaryData.viagens.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Rota</TableHead>
                      <TableHead>Distância</TableHead>
                      <TableHead>Classe</TableHead>
                      <TableHead className="text-right">Emissão CO₂</TableHead>
                      <TableHead className="text-right">MSC Gerado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {beneficiaryData.viagens.map((viagem: any) => (
                      <TableRow key={viagem.id}>
                        <TableCell>{viagem.data}</TableCell>
                        <TableCell className="font-medium">{viagem.rota}</TableCell>
                        <TableCell>{viagem.distancia}</TableCell>
                        <TableCell>{viagem.classe}</TableCell>
                        <TableCell className="text-right">{viagem.emissao}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {viagem.msc?.toFixed(2) || "0.00"} MSC
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : !isAviation && beneficiaryData.checkIns && beneficiaryData.checkIns.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Transporte</TableHead>
                      <TableHead>Distância</TableHead>
                      <TableHead className="text-right">Emissão CO₂</TableHead>
                      <TableHead className="text-right">MSC Gerado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {beneficiaryData.checkIns.map((checkIn: any) => (
                      <TableRow key={checkIn.id}>
                        <TableCell>{checkIn.data}</TableCell>
                        <TableCell className="font-medium">{checkIn.origem}</TableCell>
                        <TableCell>{checkIn.transporte}</TableCell>
                        <TableCell>{checkIn.distancia} km</TableCell>
                        <TableCell className="text-right">{checkIn.emissao.toFixed(2)} kg</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {checkIn.msc?.toFixed(2) || "0.00"} MSC
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-gray-500 py-8">Nenhum histórico encontrado</p>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerBeneficiaryDetail;
